/**
 * Fetches tenant branding from /api/people/config/branding/ on boot
 * and overrides CSS variables in tokens.css dynamically.
 *
 * Falls back to WeCo defaults if the fetch fails.
 */

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  applyTenantBrandingToDOM,
  BRANDING_DEFAULTS,
  fetchTenantBranding,
  resetTenantBrandingInDOM,
} from "@weco/suite-chrome";
import { useShellStore } from "../store";

export function useTenantBranding() {
  const getToken = useShellStore((s) => s.getValidAccessToken);
  const isAuthenticated = useShellStore((s) => s.isAuthenticated);

  // Start from the stylesheet's own colours rather than stamping the defaults
  // over them: tokens.css states a light and a dark value for each, and a
  // previous tenant's branding may still be in the document from before a
  // sign-out. Clearing leaves an unbranded org looking exactly like the
  // product ships.
  useEffect(() => {
    resetTenantBrandingInDOM();
  }, []);

  const { data: branding } = useQuery({
    queryKey: ["tenant-branding"],
    queryFn: () => fetchTenantBranding(getToken),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // A failure here used to be indistinguishable from "this tenant uses the
    // WeCo defaults", because the fetch returned them instead of throwing. It
    // throws now, so retry until it answers — a token that was not ready, or
    // People restarting, must not pin the wrong colours for the session.
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
  });

  useEffect(() => {
    if (branding) {
      applyTenantBrandingToDOM(branding);
    }
  }, [branding]);

  return branding ?? BRANDING_DEFAULTS;
}

export type { TenantBranding } from "@weco/suite-chrome";
